export type DiffSegment = { type: 'equal' | 'added' | 'removed'; text: string };

/** Above this many words on a side the quadratic LCS is skipped. */
export const MAX_DIFF_WORDS = 1500;

/**
 * Word-level LCS diff. Tokenizes on whitespace (keeping it), computes the longest common
 * subsequence of words, and emits equal/added/removed segments. Pure + deterministic — the
 * review mode renders these as green (added) / red-strike (removed) decorations.
 */
export function computeWordDiff(before: string, after: string): DiffSegment[] {
  const a = before ? before.split(/(\s+)/).filter((t) => t.length > 0) : [];
  const b = after ? after.split(/(\s+)/).filter((t) => t.length > 0) : [];
  const words = (tokens: string[]) => tokens.filter((t) => t.trim() !== '').length;
  if (words(a) > MAX_DIFF_WORDS || words(b) > MAX_DIFF_WORDS) {
    return [
      ...(before ? [{ type: 'removed' as const, text: before }] : []),
      ...(after ? [{ type: 'added' as const, text: after }] : []),
    ];
  }

  const m = a.length;
  const n = b.length;
  const lcs: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i -= 1) {
    for (let j = n - 1; j >= 0; j -= 1) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const raw: DiffSegment[] = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      raw.push({ type: 'equal', text: a[i] });
      i += 1; j += 1;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      raw.push({ type: 'removed', text: a[i] });
      i += 1;
    } else {
      raw.push({ type: 'added', text: b[j] });
      j += 1;
    }
  }
  while (i < m) { raw.push({ type: 'removed', text: a[i] }); i += 1; }
  while (j < n) { raw.push({ type: 'added', text: b[j] }); j += 1; }

  // Coalesce adjacent same-type segments into one.
  const out: DiffSegment[] = [];
  for (const seg of raw) {
    const last = out[out.length - 1];
    if (last && last.type === seg.type) last.text += seg.text;
    else out.push({ ...seg });
  }
  return out;
}

export type WordSegment = { type: 'equal' | 'added' | 'removed'; before: string; after: string };

function tokenizeWords(text: string): string[] {
  const leading = /^\s+/.exec(text)?.[0] ?? '';
  return [...(leading ? [leading] : []), ...(text.slice(leading.length).match(/\S+\s*/g) ?? [])];
}

export function diffWords(before: string, after: string): WordSegment[] {
  const a = tokenizeWords(before);
  const b = tokenizeWords(after);
  const words = (tokens: string[]) => tokens.filter((t) => t.trim() !== '').length;
  if (words(a) > MAX_DIFF_WORDS || words(b) > MAX_DIFF_WORDS) {
    return [
      ...(before ? [{ type: 'removed' as const, before, after: '' }] : []),
      ...(after ? [{ type: 'added' as const, before: '', after }] : []),
    ];
  }

  const m = a.length;
  const n = b.length;
  const same = (i: number, j: number) => a[i].trim() === b[j].trim();
  const lcs: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i -= 1) {
    for (let j = n - 1; j >= 0; j -= 1) {
      lcs[i][j] = same(i, j) ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const raw: WordSegment[] = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (same(i, j)) {
      raw.push({ type: 'equal', before: a[i], after: b[j] });
      i += 1;
      j += 1;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      raw.push({ type: 'removed', before: a[i], after: '' });
      i += 1;
    } else {
      raw.push({ type: 'added', before: '', after: b[j] });
      j += 1;
    }
  }
  while (i < m) {
    raw.push({ type: 'removed', before: a[i], after: '' });
    i += 1;
  }
  while (j < n) {
    raw.push({ type: 'added', before: '', after: b[j] });
    j += 1;
  }

  const out: WordSegment[] = [];
  for (const seg of raw) {
    const last = out[out.length - 1];
    if (last && last.type === seg.type) {
      last.before += seg.before;
      last.after += seg.after;
    } else out.push({ ...seg });
  }
  return out;
}
